import React, { useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";

import Button from "components/CustomButtons/CustomButton";
import PrintIcon from "@material-ui/icons/PictureAsPdf";
import TableRowsIcon from "@material-ui/icons/TableChart";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import { Grid } from "@material-ui/core";

function formatMoney(val) {
  return `$${Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function DailyLegacy(props) {
  const {
    title = "",
    infos = null,
    forma = {},
    timezones = {},
    report_tz = "",
    onGeneratePdf,
    onGenerateCsv,
    onBack,
  } = props;

  const formRef = useRef(null);
  const now = new Date();

  // Helper to access details in the shape used by the original PHP:
  // PHP: $infos['details'] each $info has ['agent'] and ['guest'].
  // Accept either infos.details (object) or infos as array (legacy Daily component style).
  let details = [];
  let agency = null;
  if (infos && Array.isArray(infos)) {
    // If you passed an array (like the Daily component), try to flatten to the expected shape:
    // assume each entry is an "info" similar to PHP's foreach
    details = infos;
    agency =
      infos && infos.length > 0 && infos[0].agency ? infos[0].agency : null;
  } else if (infos && typeof infos === "object") {
    if (Array.isArray(infos.details)) {
      details = infos.details;
    } else if (infos.details && typeof infos.details === "object") {
      // if details is an object keyed by something, try to map values
      details = Object.values(infos.details);
    }
    agency = infos.agency || null;
  }

  // Totals like in the PHP file (kept mutable for simplicity)
  const total = {
    agent: {
      policy_count: 0,
      cancellation_count: 0,
      premium_written: 0,
      net: 0,
      policy_fee: 0,
      iva: 0,
      total: 0,
      commision: 0,
      agent_fee: 0,
      agency_collected: 0,
      total_due: 0,
      submit_to_travco: 0,
      paypal: 0,
    },
    guest: {
      policy_count: 0,
      cancellation_count: 0,
      premium_written: 0,
      net: 0,
      policy_fee: 0,
      iva: 0,
      total: 0,
      commision: 0,
      agent_fee: 0,
      agency_collected: 0,
      total_due: 0,
      submit_to_travco: 0,
      paypal: 0,
    },
  };

  // Form submit helpers (mimic PHP jQuery behaviour if no handlers passed)
  const submitFormTo = (path) => {
    if (onGenerateCsv || onGeneratePdf) {
      // prefer handlers if provided
      if (path.endsWith("/csv") && onGenerateCsv) {
        onGenerateCsv(2, "csv");
        return;
      }
      if (path.endsWith("/pdf") && onGeneratePdf) {
        onGeneratePdf(2, "pdf");
        return;
      }
    }

    // fallback to posting the form (like original PHP)
    if (formRef.current) {
      formRef.current.action = path;
      formRef.current.submit();
    }
  };

  const handlePdf = () => submitFormTo("/reports/generate/pdf");
  const handleCsv = () => submitFormTo("/reports/generate/csv");
  const handleBack = () => {
    if (onBack) return onBack();
    window.location.href = "/reports";
  };

  return (
    <>
      {/* hidden form (like original) */}
      <form id="report_form" method="post" action="/" ref={formRef}>
        {forma &&
          Object.entries(forma).map(([k, v]) => (
            <input key={k} type="hidden" name={k} value={v} readOnly />
          ))}
      </form>

      <GridContainer style={{ paddingTop: 20 }}>
        <GridItem xs={12} sm={12} md={6}>
          <Typography variant="h6">
            {report_tz && timezones && timezones[report_tz]
              ? `${timezones[report_tz]} - ${report_tz.toUpperCase()}`
              : ""}
          </Typography>
          <Typography variant="h5">{title}</Typography>
        </GridItem>

        <GridItem xs={12} sm={12} md={6}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={4}>
              <Button
                startIcon={<PrintIcon />}
                onClick={() => onGeneratePdf(2, "pdf")}
                sx={{ ml: 2 }}
                variant="green"
              >
                PDF
              </Button>
            </GridItem>

            <GridItem xs={12} sm={12} md={4}>
              <Button
                startIcon={<TableRowsIcon />}
                onClick={() => onGenerateCsv(2, "csv")}
                sx={{ ml: 2 }}
                variant="green"
              >
                CSV
              </Button>
            </GridItem>

            <GridItem xs={12} sm={12} md={4}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ ml: 2 }}
                color="success"
                style={{
                  backgroundColor: "#FFF",
                  borderColor: "#13816F",
                  color: "#13816F",
                  border: "1px solid",
                  width: "100%",
                }}
              >
                Back To Reports
              </Button>
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={2}>
          <Typography>Date: {now.toLocaleString()}</Typography>
        </GridItem>
        <GridItem xs={12} sm={12} md={10} textAlign="right">
          <Typography variant="h6">HDI Seguros, S.A. de C.V.</Typography>
        </GridItem>
      </GridContainer>

      <Divider sx={{ mb: 2 }} />

      <GridContainer style={{ marginBottom: 16 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableBody>
              {/* Loop like PHP foreach ($infos['details'] as $info): */}
              {details && details.length > 0 ? (
                details.map((info, idx) => {
                  // Normalize shapes: agent/guest might exist
                  const agent = info.agent || {};
                  const guest = info.guest || {};

                  // accumulate totals (mutating the total object to mimic PHP)
                  total.agent.policy_count += Number(agent.policy_count || 0);
                  total.guest.policy_count += Number(guest.policy_count || 0);

                  total.agent.cancellation_count += Number(
                    agent.cancellation_count || 0
                  );
                  total.guest.cancellation_count += Number(
                    guest.cancellation_count || 0
                  );

                  total.agent.premium_written += Number(
                    agent.premium_written || 0
                  );
                  total.guest.premium_written += Number(
                    guest.premium_written || 0
                  );

                  total.agent.net += Number(agent.net || 0);
                  total.guest.net += Number(guest.net || 0);

                  total.agent.policy_fee += Number(agent.policy_fee || 0);
                  total.guest.policy_fee += Number(guest.policy_fee || 0);

                  total.agent.iva += Number(agent.iva || 0);
                  total.guest.iva += Number(guest.iva || 0);

                  total.agent.total += Number(agent.total || 0);
                  total.guest.total += Number(guest.total || 0);

                  total.agent.commision += Number(agent.commision || 0);
                  total.guest.commision += Number(guest.commision || 0);

                  total.agent.agent_fee += Number(agent.agent_fee || 0);
                  total.guest.agent_fee += Number(guest.agent_fee || 0);

                  total.agent.total_due += Number(agent.total_due || 0);
                  total.agent.paypal += Number(agent.paypal || 0);
                  total.agent.agency_collected += Number(
                    agent.agency_collected || 0
                  );
                  total.agent.percent_credit_card = Number(
                    agent.percent_credit_card || 0
                  );
                  total.agent.submit_to_travco += Number(
                    agent.submit_to_travco || 0
                  );

                  return (
                    <React.Fragment key={idx}>
                      <TableRow>
                        <TableCell style={{ width: "34%" }}>
                          <strong>Agency</strong>
                        </TableCell>
                        <TableCell colSpan={2}>
                          {agency ? agency.name : info.agency?.name}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Producer Code</strong>
                        </TableCell>
                        <TableCell colSpan={2}>
                          {agency ? agency.code : info.agency?.code}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>&nbsp;</TableCell>
                        <TableCell>
                          <strong>Agency (B)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Consumer (C)</strong>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Policy Count</strong>
                        </TableCell>
                        <TableCell>{agent.policy_count || 0}</TableCell>
                        <TableCell>{guest.policy_count || 0}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Cancellation Count</strong>
                        </TableCell>
                        <TableCell>{agent.cancellation_count || 0}</TableCell>
                        <TableCell>{guest.cancellation_count || 0}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Premium Written</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.premium_written || 0)}
                        </TableCell>
                        <TableCell>
                          {formatMoney(guest.premium_written || 0)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Net Premium</strong>
                        </TableCell>
                        <TableCell>{formatMoney(agent.net || 0)}</TableCell>
                        <TableCell>{formatMoney(guest.net || 0)}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Policy Fee</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.policy_fee || 0)}
                        </TableCell>
                        <TableCell>
                          {formatMoney(guest.policy_fee || 0)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>IVA</strong>
                        </TableCell>
                        <TableCell>{formatMoney(agent.iva || 0)}</TableCell>
                        <TableCell>{formatMoney(guest.iva || 0)}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Total Premium</strong>
                        </TableCell>
                        <TableCell>{formatMoney(agent.total || 0)}</TableCell>
                        <TableCell>{formatMoney(guest.total || 0)}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Agency Commission</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.commision || 0)}
                        </TableCell>
                        <TableCell>
                          {formatMoney(guest.commision || 0)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Agency Policy Fee</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.agent_fee || 0)}
                        </TableCell>
                        <TableCell>
                          {formatMoney(guest.agent_fee || 0)}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Total Due</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.total_due || 0)}
                        </TableCell>
                        <TableCell>&nbsp;</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Premium Collected</strong>
                        </TableCell>
                        <TableCell>{formatMoney(agent.paypal || 0)}</TableCell>
                        <TableCell>&nbsp;</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Agency Fee Collected</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.agency_collected || 0)}
                        </TableCell>
                        <TableCell>&nbsp;</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Credit card fees</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.percent_credit_card || 0)}
                        </TableCell>
                        <TableCell>&nbsp;</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <strong>Submit to Travco</strong>
                        </TableCell>
                        <TableCell>
                          {formatMoney(agent.submit_to_travco || 0)}
                        </TableCell>
                        <TableCell>&nbsp;</TableCell>
                      </TableRow>

                      {/* spacing between blocks like the <br/> in PHP */}
                      <TableRow>
                        <TableCell colSpan={3}>&nbsp;</TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No data
                  </TableCell>
                </TableRow>
              )}

              {/* final totals row (similar to PHP - you can expand to show full totals) */}
              <TableRow>
                <TableCell colSpan={1}></TableCell>
                <TableCell>
                  <strong>Agent Totals (example)</strong>
                </TableCell>
                <TableCell>
                  <strong>{formatMoney(total.agent.net)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </GridContainer>

      <GridContainer style={{ marginTop: 16 }}>
        <GridItem xs={12} sm={12} md={6}></GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <GridItem xs={12} sm={12} md={12}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={4}>
                <Button
                  startIcon={<PrintIcon />}
                  onClick={() => onGeneratePdf(2, "pdf")}
                  sx={{ ml: 2 }}
                  variant="green"
                >
                  PDF
                </Button>
              </GridItem>

              <GridItem xs={12} sm={12} md={4}>
                <Button
                  startIcon={<TableRowsIcon />}
                  onClick={() => onGenerateCsv(2, "csv")}
                  sx={{ ml: 2 }}
                  variant="green"
                >
                  CSV
                </Button>
              </GridItem>

              <GridItem xs={12} sm={12} md={4}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{ ml: 2 }}
                  color="success"
                  style={{
                    backgroundColor: "#FFF",
                    borderColor: "#13816F",
                    color: "#13816F",
                    border: "1px solid",
                    width: "100%",
                  }}
                >
                  Back To Reports
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridItem>
      </GridContainer>
    </>
  );
}

DailyLegacy.propTypes = {
  title: PropTypes.string,
  infos: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  forma: PropTypes.object,
  timezones: PropTypes.object,
  report_tz: PropTypes.string,
  onGeneratePdf: PropTypes.func,
  onGenerateCsv: PropTypes.func,
  onBack: PropTypes.func,
};
